/**
 * AIChat Surface Component
 * AI assistant peek surface
 */

import React, { useState } from 'react';
import { Stack, Text, Textarea, Group, Button, Badge, Paper, ScrollArea, Avatar } from '@mantine/core';
import { IconSend, IconUser, IconSparkles } from '@tabler/icons-react';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AIChatProps {
  phase?: number;
}

const AIChat: React.FC<AIChatProps> = ({ phase = 7 }) => {
  const [messages, setMessages] = useState<Message[]>([
    { id: '1', role: 'assistant', content: 'Hello! I am your AI assistant. How can I help you today?', timestamp: new Date() }
  ]);
  const [input, setInput] = useState('');

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages([...messages, userMessage]);
    setInput('');

    setTimeout(() => {
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I understand you said: "${input}". How can I assist you further?`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
    }, 1000);
  };

  return (
    <Stack gap="md" p="sm">
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <IconSparkles size={18} />
          <Text fw={600} size="sm">AI Assistant</Text>
        </Group>
        <Badge size="xs" variant="light" color="grape">Phase {phase}</Badge>
      </Group>

      <ScrollArea h={300} style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8, padding: 12 }}>
        <Stack gap="sm">
          {messages.map(msg => (
            <Group key={msg.id} gap="xs" align="flex-start" style={{ flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
              <Avatar size="sm" radius="xl" color={msg.role === 'user' ? 'blue' : 'grape'}>
                {msg.role === 'user' ? <IconUser size={14} /> : <IconSparkles size={14} />}
              </Avatar>
              <Paper p="xs" radius="md" style={{ maxWidth: '70%', backgroundColor: msg.role === 'user' ? 'var(--mantine-color-blue-1)' : 'var(--mantine-color-gray-1)' }}>
                <Text size="sm">{msg.content}</Text>
              </Paper>
            </Group>
          ))}
        </Stack>
      </ScrollArea>

      <Group gap="xs">
        <Textarea
          placeholder="Ask me anything..."
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          minRows={1}
          autosize
          style={{ flex: 1 }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
        />
        <Button onClick={handleSend} leftSection={<IconSend size={16} />} disabled={!input.trim()}>
          Send
        </Button>
      </Group>
    </Stack>
  );
};

export default AIChat;